import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { type SetupItem, ritualTexts } from '@/lib/company-setup';
import { createRitual, loadRitualApps, setRitualActive, updateRitualRoute } from '@/lib/workspace-rituals';
import { appRoute, COMPANY_ROUTE, TEAM_ROUTE } from '@/components/rituals/ritual-targets';
import { PLACE_ROUTES } from '@/components/profile/setup/SetupAction';
import { WEEKDAY_LABELS, describeRhythm, type WorkspaceRitual } from '@shared/types/workspace-rituals';
import type { TenantMember } from '@shared/types/teams';

interface Props {
  item: SetupItem;
  /** The ritual already added from this line, if any. */
  ritual: WorkspaceRitual | undefined;
  members: TenantMember[];
  userId: string;
  busy: boolean;
  onAdded: (ritual: WorkspaceRitual) => void;
  onRemoved: () => void;
  onSkip: (skipped: boolean) => void;
}

const SELECT = 'h-8 rounded-lg border border-domino-cardBorder bg-domino-warm px-2 text-sm text-domino-ink';

type RitualApp = { key: string; title: string };

/** What « Faire » may open: an app (its objects to handle), the team's week, the review of the apps, Mon travail. */
const openOptions = (apps: RitualApp[], current: string | null) => {
  const options = [
    ...apps.map((app) => ({ value: appRoute(app.key), label: app.title })),
    { value: TEAM_ROUTE, label: 'Le point d’équipe' },
    { value: COMPANY_ROUTE, label: 'La revue des coups' },
    { value: '/tasks', label: 'Mon travail' },
  ];
  return current && !options.some((option) => option.value === current) ? [{ value: current, label: 'Sa page actuelle' }, ...options] : options;
};

/** The default: the place the qualification gave it, an app's objects for the apps. */
const defaultRoute = (place: string | undefined, apps: RitualApp[]): string | null => {
  if (place === 'apps' && apps[0]) return appRoute(apps[0].key);
  if (place === 'objectives') return TEAM_ROUTE;
  return place ? PLACE_ROUTES[place as keyof typeof PLACE_ROUTES]?.to ?? null : null;
};

/**
 * A ritual of the setup: who holds it, which days, then « Ajouter aux tâches »
 * puts it in their Aujourd'hui on those days. In place once added.
 */
export const RitualRow = ({ item, ritual, members, userId, busy, onAdded, onRemoved, onSkip }: Props) => {
  const usage = item.source.list === 'usage' ? item.source.entry : null;
  const texts = ritualTexts(usage?.what ?? item.title);
  const [title, setTitle] = useState(texts.title);
  const [rhythm, setRhythm] = useState<'weekly' | 'monthly'>(usage?.rhythm === 'monthly' ? 'monthly' : 'weekly');
  const [days, setDays] = useState<number[]>(usage?.rhythm === 'daily' ? [1, 2, 3, 4, 5] : [1]);
  const [owner, setOwner] = useState(userId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apps, setApps] = useState<RitualApp[]>([]);
  const [route, setRoute] = useState<string | null | undefined>(undefined);
  const active = ritual?.active ? ritual : undefined;
  useEffect(() => { void loadRitualApps().then(setApps).catch(() => setApps([])); }, []);
  const chosenRoute = route === undefined ? defaultRoute(usage?.place, apps) : route;
  const ownerName = (id: string) => (id === userId ? 'vous' : members.find((member) => member.user_id === id)?.display_name ?? 'une personne');

  const toggleDay = (day: number) => setDays((current) => (rhythm === 'monthly'
    ? [day]
    : current.includes(day) ? current.filter((entry) => entry !== day) : [...current, day].sort()));

  const add = async () => {
    setSaving(true); setError(null);
    try {
      onAdded(await createRitual({
        ownerUserId: owner, title: title.trim(), detail: texts.detail, rhythm, days,
        route: chosenRoute, sourceUsageId: item.id,
      }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le rituel n’a pas été ajouté.');
    } finally {
      setSaving(false);
    }
  };

  const changeRoute = async (next: string) => {
    if (!active) return;
    setSaving(true); setError(null);
    try { onAdded(await updateRitualRoute(active.id, next)); } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le rituel n’a pas été mis à jour.');
    } finally { setSaving(false); }
  };
  const openSelect = (value: string | null, onSelect: (next: string) => void) => (
    <select aria-label="Ouvre" value={value ?? ''} onChange={(event) => onSelect(event.target.value)} className={SELECT} disabled={saving}>
      {!value && <option value="">Le chat</option>}
      {openOptions(apps, value).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
  );

  const remove = async () => {
    if (!active) return;
    setSaving(true); setError(null);
    try { await setRitualActive(active.id, false); onRemoved(); } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le rituel n’a pas été retiré.');
    } finally { setSaving(false); }
  };

  return (
    <li className={cn('space-y-3 border-t border-domino-cardBorder py-4 first:border-t-0', item.skipped && 'opacity-50')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={cn('text-sm font-medium leading-6 text-domino-ink', item.skipped && 'line-through')}>{item.title}</p>
          <p className="text-xs leading-5 text-domino-inkSoft">{item.detail}</p>
        </div>
        {!active && (
          <button type="button" onClick={() => onSkip(!item.skipped)} disabled={busy || saving} className="shrink-0 text-xs text-domino-inkSoft hover:text-domino-ink hover:underline">
            {item.skipped ? 'Reprendre' : 'Passer'}
          </button>
        )}
      </div>
      {active && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          <span>Dans les tâches de {ownerName(active.owner_user_id)}, {describeRhythm(active)}.</span>
          {openSelect(active.route, (next) => void changeRoute(next))}
          <button type="button" onClick={() => void remove()} disabled={busy || saving} className="text-xs underline-offset-2 hover:underline">Retirer des tâches</button>
        </div>
      )}
      {!active && !item.skipped && (
        <div className="space-y-2 rounded-lg bg-white/70 px-3 py-3">
          <Input value={title} onChange={(event) => setTitle(event.target.value)} aria-label="Nom du rituel" maxLength={180} />
          <div className="flex flex-wrap items-center gap-2">
            <select aria-label="Rythme" value={rhythm} onChange={(event) => { const next = event.target.value as 'weekly' | 'monthly'; setRhythm(next); if (next === 'monthly') setDays([days[0] ?? 1]); }} className={SELECT}>
              <option value="weekly">Chaque semaine</option>
              <option value="monthly">Une fois par mois</option>
            </select>
            <div className="flex gap-1" role="group" aria-label="Jours">
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <button
                  key={day}
                  type="button"
                  aria-pressed={days.includes(day)}
                  aria-label={WEEKDAY_LABELS[day]!.long}
                  onClick={() => toggleDay(day)}
                  className={cn('h-8 w-8 rounded-full border text-xs', days.includes(day) ? 'border-domino-aubergine bg-domino-lilac font-semibold text-domino-aubergine' : 'border-domino-cardBorder text-domino-inkSoft')}
                >
                  {WEEKDAY_LABELS[day]!.short}
                </button>
              ))}
            </div>
            {members.length > 1 && (
              <select aria-label="Pour qui" value={owner} onChange={(event) => setOwner(event.target.value)} className={SELECT}>
                {members.map((member) => <option key={member.user_id} value={member.user_id}>{member.user_id === userId ? 'Moi' : member.display_name ?? 'Sans nom'}</option>)}
              </select>
            )}
            {openSelect(chosenRoute, (next) => setRoute(next || null))}
            <Button size="sm" onClick={() => void add()} loading={saving} disabled={busy || saving || !title.trim() || days.length === 0}>Ajouter aux tâches</Button>
          </div>
          {days.length > 0 && <p className="text-xs text-domino-inkSoft">Dans Mon travail {describeRhythm({ rhythm, days })}.</p>}
          {error && <p className="text-sm text-red-700">{error}</p>}
        </div>
      )}
    </li>
  );
};
